import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { uploadVerificationDocument, DOCUMENT_COMPLIANCE_CHECKLISTS } from '../../../services/notairePortalService';

const DOCUMENT_TYPES = [
  { value: 'titre_propriete', label: 'Titre de propriété', icon: 'FileText', color: 'text-amber-600', bg: 'bg-amber-50' },
  { value: 'identite', label: "Pièce d\'identité", icon: 'CreditCard', color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'plan', label: 'Plan cadastral / Survey', icon: 'Map', color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'acte', label: 'Acte notarié', icon: 'ScrollText', color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'fiscal', label: 'Document fiscal', icon: 'Receipt', color: 'text-rose-600', bg: 'bg-rose-50' },
  { value: 'autre', label: 'Autre document', icon: 'File', color: 'text-slate-600', bg: 'bg-slate-50' },
];

const UPLOADER_ROLES = [
  { value: 'acheteur', label: 'Acheteur' },
  { value: 'vendeur', label: 'Vendeur' },
  { value: 'notaire', label: 'Notaire' },
  { value: 'partie', label: 'Partie tierce' },
];

const DocumentUploadModal = ({ dossierId, onClose, onUploaded }) => {
  const [step, setStep] = useState(1); // 1: type selection, 2: file + checklist, 3: uploading
  const [selectedType, setSelectedType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [uploaderRole, setUploaderRole] = useState('partie');
  const [file, setFile] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const selectedTypeConfig = DOCUMENT_TYPES?.find(t => t?.value === selectedType);
  const checklistItems = DOCUMENT_COMPLIANCE_CHECKLISTS?.[selectedType] || [];
  const completedItems = Object.values(checklist)?.filter(Boolean)?.length;
  const complianceScore = checklistItems?.length > 0 ? Math.round((completedItems / checklistItems?.length) * 100) : 0;

  const handleTypeSelect = (type) => {
    setSelectedType(type?.value);
    const defaultName = type?.label;
    setDocumentName(defaultName);
    // Initialize checklist
    const items = DOCUMENT_COMPLIANCE_CHECKLISTS?.[type?.value] || [];
    setChecklist(items?.reduce((acc, item) => ({ ...acc, [item?.key]: false }), {}));
    setStep(2);
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (selectedFile?.size > maxSize) {
      setError('Le fichier ne doit pas dépasser 20 Mo.');
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed?.includes(selectedFile?.type)) {
      setError('Format non supporté. Utilisez PDF, JPG, PNG ou DOCX.');
      return;
    }
    setError('');
    setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const droppedFile = e?.dataTransfer?.files?.[0];
    if (droppedFile) handleFileChange(droppedFile);
  };

  const handleChecklistToggle = (key) => {
    setChecklist(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSubmit = async () => {
    if (!file || !documentName || !selectedType) {
      setError('Veuillez remplir tous les champs et sélectionner un fichier.');
      return;
    }
    setUploading(true);
    setError('');
    const { data, error: uploadError } = await uploadVerificationDocument(
      dossierId, file, documentName, selectedType, uploaderRole
    );
    setUploading(false);
    if (uploadError) {
      setError(uploadError);
      return;
    }
    onUploaded?.();
    onClose?.();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-400';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center">
              <Icon name="Upload" size={17} className="text-brand-earth" />
            </div>
            <div>
              <h3 className="text-ios-headline font-display font-display-semibold text-text-primary">
                {step === 1 ? 'Type de document' : 'Téléverser un document'}
              </h3>
              <p className="text-ios-caption1 text-text-secondary font-system">
                {step === 1 ? 'Sélectionnez le type de pièce à déposer' : selectedTypeConfig?.label}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {/* Step 1: Type Selection */}
          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              {DOCUMENT_TYPES?.map(type => (
                <button
                  key={type?.value}
                  onClick={() => handleTypeSelect(type)}
                  className="flex flex-col items-start p-4 rounded-2xl border-2 border-border/50 hover:border-brand-gold hover:bg-brand-gold/5 transition-all group text-left">
                  <div className={`w-10 h-10 rounded-xl ${type?.bg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon name={type?.icon} size={18} className={type?.color} />
                  </div>
                  <span className="text-ios-callout font-system font-body-semibold text-text-primary leading-tight">
                    {type?.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: File + Checklist */}
          {step === 2 && (
            <div className="space-y-5">
              {/* Back button */}
              <button
                onClick={() => { setStep(1); setFile(null); setError(''); }}
                className="flex items-center space-x-1.5 text-ios-caption1 font-system text-text-secondary hover:text-brand-earth transition-colors">
                <Icon name="ArrowLeft" size={14} />
                <span>Changer de type</span>
              </button>

              {/* Document name */}
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                  Nom du document *
                </label>
                <input
                  type="text"
                  value={documentName}
                  onChange={e => setDocumentName(e?.target?.value)}
                  placeholder="Ex: Titre de propriété - Villa Cocody"
                  className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                />
              </div>

              {/* Uploader role */}
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                  Déposé par *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {UPLOADER_ROLES?.map(role => (
                    <button
                      key={role?.value}
                      onClick={() => setUploaderRole(role?.value)}
                      className={`py-2.5 px-3 rounded-xl border text-ios-caption1 font-system font-body-medium transition-all ${
                        uploaderRole === role?.value
                          ? 'border-brand-gold bg-brand-gold/10 text-brand-earth' :'border-border text-text-secondary hover:border-brand-gold/50'
                      }`}>
                      {role?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* File drop zone */}
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                  Fichier *
                </label>
                <div
                  onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef?.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${
                    dragOver ? 'border-brand-gold bg-brand-gold/5' : file ? 'border-green-400 bg-green-50' : 'border-border hover:border-brand-gold/60 hover:bg-slate-50'
                  }`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={e => handleFileChange(e?.target?.files?.[0])}
                  />
                  {file ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                        <Icon name="FileCheck" size={18} className="text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate max-w-[200px]">{file?.name}</p>
                        <p className="text-ios-caption1 text-text-secondary">{(file?.size / 1024 / 1024)?.toFixed(2)} Mo</p>
                      </div>
                      <button
                        onClick={e => { e?.stopPropagation(); setFile(null); }}
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                        <Icon name="X" size={14} className="text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                        <Icon name="CloudUpload" size={22} className="text-text-secondary" />
                      </div>
                      <p className="text-ios-callout font-system font-body-medium text-text-primary">
                        Glissez votre fichier ici
                      </p>
                      <p className="text-ios-caption1 text-text-secondary mt-1">
                        PDF, JPG, PNG, DOCX — max 20 Mo
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Compliance Checklist */}
              {checklistItems?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-ios-callout font-system font-body-medium text-text-primary">
                      Checklist de conformité
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className={`text-ios-caption1 font-system font-body-semibold ${getScoreColor(complianceScore)}`}>
                        {complianceScore}%
                      </span>
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${getScoreBg(complianceScore)}`}
                          style={{ width: `${complianceScore}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 bg-slate-50 rounded-2xl p-3">
                    {checklistItems?.map(item => (
                      <label
                        key={item?.key}
                        className="flex items-start space-x-3 cursor-pointer group">
                        <div
                          onClick={() => handleChecklistToggle(item?.key)}
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                            checklist?.[item?.key]
                              ? 'bg-brand-earth border-brand-earth' :'border-border group-hover:border-brand-gold'
                          }`}>
                          {checklist?.[item?.key] && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
                        </div>
                        <span
                          onClick={() => handleChecklistToggle(item?.key)}
                          className={`text-ios-caption1 font-system leading-relaxed transition-colors ${
                            checklist?.[item?.key] ? 'text-text-primary line-through text-text-secondary' : 'text-text-primary'
                          }`}>
                          {item?.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-text-secondary font-system mt-2">
                    {completedItems}/{checklistItems?.length} critères validés — le notaire peut compléter cette vérification
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-ios-caption1 text-red-600 font-system">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-6 pb-6 pt-4 border-t border-border/50 flex-shrink-0 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-border rounded-xl text-ios-callout font-system font-body-medium text-text-secondary hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || !file || !documentName}
              className="flex-1 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 disabled:opacity-60 transition-colors flex items-center justify-center space-x-2">
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Téléversement...</span>
                </>
              ) : (
                <>
                  <Icon name="Upload" size={15} />
                  <span>Déposer le document</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploadModal;
